// THE SAVED-VIEWS CONTROLLER: the one hook a list screen calls to adopt saved
// views (spec 16 §3.12 and §4, D-305 rulings 3 to 9 and 17 to 21). The rules
// themselves are pure and live in `../lib/view-state.ts`; this file holds the
// state they need across renders (the table's columns, the arrival, the
// notices, the refusals the running screen remembers) and performs the writes.
//
// WHAT A SCREEN GIVES IT: its screen key, its validated URL search and the
// setter that writes the address (replacing the history entry), its own URL
// validator run with the reader's verdict, and its DataTable columns with the
// loaded rows. The URL keys of its filters and its order parameter with the
// declared default come from `../lib/screens.ts`, pinned to the contract. WHAT IT GIVES BACK: `listEnabled`, which the screen's list
// query must honour; `settled`, which with the list's own settle reveals the
// switcher and the table together (never before an arrival's columns apply); `write`, through which every filter and
// sort change goes, since the order-token rule judges each write;
// `dropRefusedOrder`, which both arms of D-301's fallback call;
// `columnConfig`, which goes straight to the DataTable; and the controller
// `<ViewSwitcher views={…}>` renders. The screen adds `view` to its route's
// `validateSearch` and otherwise keeps its URL grammar as it is.
//
// ARRIVAL (item 5). The screen mounting with `view` in the address, or
// `view` changing by navigation, is an arrival. On an address holding `view`
// and no filter or sort parameter, the stored config is applied and written
// into the address, and until that address has landed the list query stays
// off (`listEnabled` false), so the first list page is the page read with the
// applied state and the bare list is never read first (ruling 21). Beside any
// filter or sort parameter, the address decides the filters and the order,
// the list goes at once, and only the view's columns apply. When the views
// read fails, the list reads from the address, and a successful Retry is an
// arrival. A press of a chip applies the view directly and records it as
// arrived, so the navigation it writes is not applied a second time.
//
// OFFLINE (R2, PR-E4 wave 4). A views read react-query has paused for want of
// a network, with nothing in the cache, is a failed read here: `status` is
// "error", `offline` is true, the row says so with Retry, and the list opens
// from the address exactly as it opens after a failed read. Read as pending,
// it held the list gate and the reveal shut for as long as the network was
// gone. When the network returns the paused read resumes, and its answer is a
// successful Retry.
//
// RESET (R1). `canReset` is true exactly when pressing Reset would change the
// address or what the table renders: with a view active, when the address or
// the columns differ from the view as Reset would re-apply it; with none, when
// the address holds `view`, a filter or an order, or the columns render other
// than the defaults. It is offered only once the views read has answered and
// any arrival has applied: while the read is pending or has failed there is
// no Reset at all (W5, PR-E4 wave 5), since the row then shows a skeleton or
// one sentence with Retry and nothing else.
//
// SETTLED (W1, PR-E4 wave 5). The views read answering is not enough to reveal
// the region. An arrival applies the view's columns in the effect that runs
// after the read's first successful render, so for that render the table
// would paint the default columns and Reset would show for columns about to
// change. `settled` stays false while an arrival for the address's view is
// still waiting, or failed earlier and has not been applied since a
// successful read, and `canReset` stays false with it.
//
// A RETRY KEEPS ITS ROW (W4, PR-E4 wave 5). React Query puts a query that
// holds no data back to `pending` when it reads again, so a Retry of a failed
// read, or a re-read on window focus after a 403, would show the loading row
// until the answer came. The controller remembers the last failure and keeps
// `status` "error" with that failure while a re-read runs, so the Retry the
// reader pressed stays on screen, busy, and a row hidden by a 403 stays
// hidden.
//
// ONE PENDING TARGET (V1, PR-E4 wave 6; W7 of wave 5 generalised). The
// router commits an address a render after it is written, while the columns
// a write sets are state and apply at once. So every address this hook writes
// goes through one function, `navigate`, which records the address it is
// about to write before it calls the screen's setter: a chip press, All, an
// un-press, More views, Reset, Save as new, a delete of the active view, the
// screen's own filter and sort writes, D-301's drop, an arrival's apply (on
// mount, after a successful Retry, when the network returns), and the strip
// of a view that no longer exists. Until the router's address moves off the
// one the write was made from, Reset, Modified, the lit chip, the active view
// and the reveal read that target, so none of them shows, for even one
// render, a state the reader is not about to see. The setter has exactly one
// call site, `navigate`, and `use-saved-views.test.tsx` counts it.
//
// A 403 STAYS HIDDEN (V3). A views read refused 403 hides the row, and the
// refusal is remembered until a read succeeds: a re-read on window focus, one
// paused offline, or one that fails another way all keep it.
//
// OFFLINE MEANS UNKNOWN (V6). A list served from the cache while the browser
// is offline may predate the view the address names, so a view it does not
// hold is not announced as gone. The arrival reads as a failed one: the list
// opens from the address and the region settles, and the view applies, or is
// found gone, when a read answers online.

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { onlineManager } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  mergeColumnConfig,
  sameColumnLayout,
  type ColumnConfigEntry,
  type DataTableColumn,
  type DataTableColumnConfig,
} from "../../../../components/data-table";
import { useDesktop } from "../../../../components/use-desktop";
import { RequestError } from "../../../../lib/request";
import {
  addressKey,
  addressWithoutOrder,
  addressWithoutView,
  appliedAddress,
  asSearch,
  bareAddress,
  chipRow,
  configFromState,
  groupViews,
  isAllPressed,
  isChipPressed,
  isViewAlone,
  isViewModified,
  normalizeOrder,
  orderFencedByPresence,
  resolveView,
  viewParamOf,
  withOrderToken,
  type AppliedView,
  type ChipRow,
  type OrderFence,
  type SavedView,
  type SavedViewScreenKey,
  type SearchRecord,
  type ViewGroups,
  type ViewScreenSpec,
} from "../lib/view-state";
import { SAVED_VIEW_SCREENS_WEB, type SavedViewScreenWeb } from "../lib/screens";
import {
  useCreateSavedView,
  useDeleteSavedView,
  useSavedViewList,
  useUpdateSavedView,
  type SavedViewCreateRequest,
  type SavedViewPatchRequest,
} from "./saved-views-queries";

/** What an adopting screen passes in. */
export interface SavedViewsOptions<S extends object, Row, K extends string = string> {
  /** The screen key the contracts registry names (`sales.quotes`, …). */
  readonly screenKey: SavedViewScreenKey;
  /** The validated URL search. Its route's `validateSearch` must carry
   * `view` as a string. */
  readonly search: S;
  /** Writes the address, replacing the history entry, as the screens' filter
   * writes already do. The hook calls it from `navigate` alone (V1). */
  readonly setSearch: (next: S) => void;
  /** The route's own validator, run with the reader's verdict (D-301), so a
   * stored money order a reader is known not to be sent is dropped at apply.
   * It must drop what it cannot accept, never throw, and carry `view`. The
   * screen's filter keys and its order parameter come from
   * `SAVED_VIEW_SCREENS_WEB`, which a test holds equal to the contracts
   * registry. */
  readonly validate: (raw: Record<string, unknown>) => S;
  /** The DataTable's declared columns, with `presence` on every redactable
   * one (design-system §3.3). */
  readonly columns: readonly DataTableColumn<Row, K>[];
  /** The rows the table shows, for `presence` and the chooser. */
  readonly rows: readonly Row[];
  /** The list read is in flight: the chooser is disabled. */
  readonly rowsLoading: boolean;
  /** The words the screen's own filter bar names each filter by, keyed by
   * URL key. A refusal that names a stored filter (R7) uses them, so each
   * screen names its filters as its bar does: the work-order list in the
   * tenant's terminology (W8), the sales books with their bars' labels (V5).
   * A key left out falls back to `views.filters`. */
  readonly filterLabels?: Readonly<Partial<Record<string, string>>>;
}

export type ViewNotice = "stale" | "gone";

/** Where the screen is with the view its address named on arrival. */
interface Arrival {
  readonly id: string;
  readonly phase: "waiting" | "failed" | "applied" | "missing";
}

/** The address the hook's last write asked for, validated as the route will
 * hold it, and the address it was written from (V1). */
interface PendingTarget {
  readonly from: string;
  readonly target: SearchRecord;
  /** An arrival's own write on `view` alone: the list waits for it to land. */
  readonly holdsList: boolean;
}

export interface SaveAsNewInput {
  readonly name: string;
  readonly shared: boolean;
  /** The key the form holds for this write (V2, `../lib/write-key.ts`). */
  readonly idempotencyKey: string;
}

/** What the hook gives back. The first block is for the screen, the rest for
 * `<ViewSwitcher>`. */
export interface SavedViewsController<S extends object, Row, K extends string = string> {
  /** The list query may be issued. False while an arrival on `?view=<id>`
   * alone waits for the views read and for its applied address to land. */
  readonly listEnabled: boolean;
  /** The views read has answered, either way, no arrival it answered is
   * still to apply its columns (W1), and no address an arrival wrote is still
   * to land (V1). The screen reveals the switcher and the table together once
   * this and its first page settle. */
  readonly settled: boolean;
  /** Every filter or sort write the screen makes goes through here. */
  readonly write: (next: S) => void;
  /** D-301's fallback: take the order out of the address, keep `view` and
   * every filter, and remember a refusal of the active view's own order. */
  readonly dropRefusedOrder: (refusedToken?: string) => void;
  /** Pass to `<DataTable columnConfig={…}>`. */
  readonly columnConfig: DataTableColumnConfig;
  /** The view the address names, once the views read holds it: the address
   * the hook's last write asked for until the router's has moved (V1). */
  readonly activeView: SavedView | undefined;
  /** The reader's state differs from the active view as they can apply it. */
  readonly modified: boolean;

  readonly spec: ViewScreenSpec;
  /** The views read. A read paused offline with nothing cached is "error",
   * with `offline` true (R2), a re-read after a failure stays "error", with
   * `retrying` true, until it answers (W4), and a read refused 403 stays
   * "error" with that refusal until a read succeeds (V3). */
  readonly status: "pending" | "error" | "success";
  readonly error: unknown;
  /** The views read is paused for want of a network and holds nothing. */
  readonly offline: boolean;
  /** Pressing Reset would change the address or the rendered columns (R1). */
  readonly canReset: boolean;
  readonly groups: ViewGroups;
  readonly row: ChipRow;
  readonly isPressed: (view: SavedView) => boolean;
  readonly allPressed: boolean;
  readonly notice: ViewNotice | undefined;
  /** The address names a view (for the error sentence). */
  readonly namesView: boolean;
  readonly desktop: boolean;
  /** The screen's own name for a filter, when it gave one (W8, V5). */
  readonly filterLabel: (key: string) => string | undefined;
  readonly columns: readonly DataTableColumn<Row, K>[];
  readonly rows: readonly Row[];
  readonly rowsLoading: boolean;
  readonly columnState: readonly ColumnConfigEntry[];
  readonly setColumnState: (next: readonly ColumnConfigEntry[]) => void;
  readonly press: (view: SavedView) => void;
  /** More views: applies a view, and the applied one again as Reset does
   * (V6). */
  readonly apply: (view: SavedView) => void;
  readonly pressAll: () => void;
  readonly reset: () => void;
  readonly retry: () => void;
  /** A Retry of the failed views read, or a re-read of a refused one, is in
   * flight. */
  readonly retrying: boolean;
  /** Each write takes the Idempotency-Key its form holds (V2). */
  readonly save: (idempotencyKey: string) => Promise<void>;
  readonly saveAsNew: (input: SaveAsNewInput) => Promise<SavedView>;
  readonly rename: (id: string, name: string, idempotencyKey: string) => Promise<void>;
  readonly setShared: (id: string, shared: boolean, idempotencyKey: string) => Promise<void>;
  readonly remove: (id: string, idempotencyKey: string) => Promise<void>;
  readonly saving: boolean;
}

/** A width change for one column: the entry keeps its place, or joins the
 * end when the configuration did not name it. */
function resizedEntries(
  entries: readonly ColumnConfigEntry[],
  key: string,
  width: number | undefined,
): ColumnConfigEntry[] {
  const current = entries.find((entry) => entry.key === key);
  const next: ColumnConfigEntry = { key };
  if (current?.hidden !== undefined) next.hidden = current.hidden;
  if (width !== undefined) next.width = width;
  return mergeColumnConfig(entries, [next]);
}

/** A read the server refused for this reader (V3): its row stays hidden. */
function isRefusedRead(error: unknown): boolean {
  return error instanceof RequestError && error.httpStatus === 403;
}

function subscribeOnline(listener: () => void): () => void {
  return onlineManager.subscribe(listener);
}

function isOnline(): boolean {
  return onlineManager.isOnline();
}

export function useSavedViews<S extends object, Row, K extends string = string>(
  options: SavedViewsOptions<S, Row, K>,
): SavedViewsController<S, Row, K> {
  const { t } = useTranslation();
  const desktop = useDesktop();
  const { screenKey, search, validate, columns, rows, rowsLoading, filterLabels } = options;
  const screen: SavedViewScreenWeb = SAVED_VIEW_SCREENS_WEB[screenKey];
  const filterKeys = screen.filterKeys;
  const orderParam = screen.order.param;
  const declaredDefault = screen.order.declaredDefault;

  const spec = useMemo<ViewScreenSpec>(
    () => ({
      screenKey,
      filterKeys,
      orderParam,
      declaredDefaultOrder: declaredDefault,
      validate,
    }),
    [screenKey, filterKeys, orderParam, declaredDefault, validate],
  );

  const current = asSearch(search);
  const viewParam = viewParamOf(current);
  const nowKey = addressKey(spec, current);

  const list = useSavedViewList(screenKey);
  const create = useCreateSavedView(screenKey);
  const update = useUpdateSavedView(screenKey);
  const del = useDeleteSavedView(screenKey);
  const online = useSyncExternalStore(subscribeOnline, isOnline, isOnline);

  const items = useMemo(
    () => (list.data?.items ?? []).filter((view) => view.screen_key === screenKey),
    [list.data, screenKey],
  );
  const groups = useMemo(() => groupViews(items, screenKey), [items, screenKey]);

  const [columnState, setColumnState] = useState<readonly ColumnConfigEntry[]>([]);
  const [notice, setNotice] = useState<ViewNotice | undefined>(undefined);
  const [refused, setRefused] = useState<ReadonlySet<string>>(() => new Set());
  const [arrival, setArrival] = useState<Arrival | null>(() =>
    viewParam === undefined ? null : { id: viewParam, phase: "waiting" },
  );
  const [seenView, setSeenView] = useState<string | undefined>(viewParam);
  // Views this screen deleted itself: finding one missing says nothing.
  const suppressGone = useRef(new Set<string>());
  // The screen is mounted. Set again on every mount, so StrictMode's
  // unmount-and-remount leaves it true.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // A D-301 drop that arrived before the views read answered.
  const [pendingDrop, setPendingDrop] = useState<{ readonly token: string | undefined } | null>(
    null,
  );
  // The last failure of the views read, kept while a re-read runs (W4), and a
  // refusal kept until a read succeeds (V3).
  const [failure, setFailure] = useState<{ readonly error: unknown } | null>(null);
  // The address the hook's last write asked for (V1).
  const [pending, setPending] = useState<PendingTarget | null>(null);

  // Arrival: `view` changed by navigation (or the screen mounted with it).
  // Derived during render, so the gate below holds on this very render.
  if (seenView !== viewParam) {
    setSeenView(viewParam);
    if (viewParam === undefined) {
      if (arrival !== null) setArrival(null);
    } else if (arrival?.id !== viewParam) {
      setArrival({ id: viewParam, phase: "waiting" });
    }
  }
  // The target holds until the router's address moves off the address it was
  // written from. From then on the router's address is what the reader sees.
  const pendingNow = pending !== null && nowKey === pending.from ? pending : null;
  if (pending !== null && pendingNow === null) setPending(null);
  const shown: SearchRecord = pendingNow?.target ?? current;
  const shownViewParam = viewParamOf(shown);
  const shownKey = addressKey(spec, shown);

  const fenceFor = (view: SavedView, refusedSet: ReadonlySet<string> = refused): OrderFence => ({
    refused: refusedSet.has(view.id),
    absentColumn: orderFencedByPresence(columns, rows, view.config.order),
  });
  const resolveFor = (view: SavedView, refusedSet?: ReadonlySet<string>): AppliedView =>
    resolveView(spec, view.config, fenceFor(view, refusedSet));

  // The read's state as the row reads it. A refusal (403) is remembered until
  // a read succeeds (V3). Any other failure is remembered while a re-read
  // runs (W4), since React Query answers a query with no data `pending` again
  // the moment it refetches, and a read paused offline with nothing cached
  // settles as a failed one (R2).
  const refusal = failure !== null && isRefusedRead(failure.error) && list.status !== "success";
  if (list.status === "success" && failure !== null) setFailure(null);
  if (list.status === "error" && failure?.error !== list.error && !refusal) {
    setFailure({ error: list.error });
  }
  const offline = !refusal && list.status === "pending" && list.fetchStatus === "paused";
  const rereading = !refusal && list.status === "pending" && !offline && failure !== null;
  const status: "pending" | "error" | "success" =
    refusal || offline || rereading ? "error" : list.status;
  const readError: unknown = refusal
    ? failure.error
    : offline
      ? null
      : rereading
        ? failure.error
        : list.error;
  const fetching = list.isFetching;
  // A list the browser cannot check against the server (V6): the cache is
  // all there is, and it may predate the view the address names.
  const unverified = list.fetchStatus === "paused" || !online;
  const holdsShownView = shownViewParam !== undefined && items.some((i) => i.id === shownViewParam);
  // Offline, a view the cached list does not hold is unknown, never gone.
  const unknownOffline =
    status === "success" && shownViewParam !== undefined && !holdsShownView && unverified;

  /** THE ONE ADDRESS WRITE (V1). It records the address it is about to write,
   * validated as the route will hold it, and the address it is written from,
   * then calls the screen's setter. `holdsList` keeps the list waiting until
   * the target lands (an arrival's apply on `view` alone, ruling 21). A
   * screen the reader has left writes nothing: Save as new and a delete call
   * this after their request answers, and by then the reader may be on
   * another screen, which the setter would pull back to this one. */
  const navigate = (target: Record<string, unknown>, holdsList = false): void => {
    if (!mounted.current) return;
    const { spec: s, current: now, options: o } = latest.current;
    const from = addressKey(s, now);
    const landing = asSearch(s.validate({ ...target }));
    setPending(addressKey(s, landing) !== from ? { from, target: landing, holdsList } : null);
    o.setSearch(target as S);
  };

  // Reconcile the address with the views read: apply an arrival, and say so
  // when the view the address names no longer resolves (item 9). It reads the
  // address the reader is about to see, so a write in flight is not undone.
  useEffect(() => {
    const { spec: s, shown: now, navigate: go, resolveFor: resolve } = latest.current;
    const id = viewParamOf(now);
    if (id === undefined) return;
    if (status === "error") {
      if (arrival?.id === id && arrival.phase === "waiting") setArrival({ id, phase: "failed" });
      return;
    }
    if (status !== "success") return;
    const view = items.find((item) => item.id === id);
    if (view === undefined) {
      // A list served from the cache while a fresh read is in flight may
      // predate the view; only a settled read says it no longer resolves.
      if (fetching) return;
      // Offline, the list cannot be checked: the arrival reads as failed, so
      // the list opens from the address, and nothing is said to be gone (V6).
      if (unverified) {
        if (arrival?.id === id && arrival.phase === "waiting") setArrival({ id, phase: "failed" });
        return;
      }
      if (arrival?.id === id && arrival.phase === "missing") return;
      if (!suppressGone.current.has(id)) setNotice("gone");
      setColumnState([]);
      setArrival({ id, phase: "missing" });
      go(addressWithoutView(now));
      return;
    }
    if (arrival?.id !== id) return;
    if (arrival.phase !== "waiting" && arrival.phase !== "failed") return;
    const applied = resolve(view);
    setColumnState(view.config.columns);
    if (isViewAlone(s, now)) {
      setNotice(applied.staleFilters.length > 0 ? "stale" : undefined);
      const target = appliedAddress(s, now, view.id, applied);
      if (addressKey(s, target) !== addressKey(s, now)) go(target, true);
    }
    setArrival({ id, phase: "applied" });
  }, [shownViewParam, status, fetching, unverified, items, arrival]);

  const alone = isViewAlone(spec, current);
  const waitingForViews =
    viewParam !== undefined &&
    alone &&
    status !== "error" &&
    !unknownOffline &&
    (arrival?.id !== viewParam || arrival.phase === "waiting");
  const waitingForAddress = pendingNow?.holdsList === true;
  const listEnabled = !waitingForViews && !waitingForAddress;

  // An arrival for the address's view is still to apply after a successful
  // read (W1): its columns land in the effect above, a render from now. A
  // view the offline cache cannot find is not waited for (V6).
  const arrivalPending =
    status === "success" &&
    viewParam !== undefined &&
    arrival?.id === viewParam &&
    (arrival.phase === "waiting" || arrival.phase === "failed") &&
    !unknownOffline;

  // The view the shown address names (V1): the router's, or the one the
  // hook's last write asked for.
  const activeView =
    shownViewParam !== undefined && status === "success"
      ? items.find((item) => item.id === shownViewParam)
      : undefined;
  const arrived =
    arrival !== null &&
    arrival.id === shownViewParam &&
    arrival.phase === "applied" &&
    !waitingForAddress;
  let modified = false;
  if (activeView !== undefined && arrived) {
    const applied = resolveFor(activeView);
    const columnsSame = sameColumnLayout(columns, rows, columnState, activeView.config.columns);
    modified = isViewModified(spec, shown, applied, columnsSame);
  }

  const applyView = (view: SavedView, refusedSet?: ReadonlySet<string>) => {
    const applied = resolveFor(view, refusedSet);
    setColumnState(view.config.columns);
    setNotice(applied.staleFilters.length > 0 ? "stale" : undefined);
    setArrival({ id: view.id, phase: "applied" });
    navigate(appliedAddress(spec, shown, view.id, applied));
  };

  /** Back to the bare list (All, or the pressed chip pressed again). Clearing
   * an active view returns the columns to the screen's defaults; with no
   * view active the columns are left alone (item 6). */
  const clearToBare = () => {
    setNotice(undefined);
    if (shownViewParam !== undefined) setColumnState([]);
    navigate(bareAddress(spec, shown));
  };

  const isPressed = (view: SavedView) => isChipPressed(spec, shown, view, resolveFor(view));

  const write = (next: S) => {
    const record = asSearch(next);
    const id = viewParamOf(record);
    const view =
      id !== undefined && status === "success" ? items.find((item) => item.id === id) : undefined;
    navigate(withOrderToken(spec, record, view === undefined ? undefined : resolveFor(view)));
  };

  /** D-301's fallback, performed: the order leaves the address and `view`
   * and every filter stay, under the order-token rule (ruling 7). A refusal
   * of the active view's own stored order is remembered, so that order counts
   * as fenced from then on. A view the settled read no longer holds leaves
   * with it, since the reconcile above is removing it in the same turn. */
  const performDrop = (refusedToken: string | undefined) => {
    let refusedSet = refused;
    // The server refused the order of the address it was sent, so the view
    // is the one the router's address names, whatever a press wrote since.
    const view =
      viewParam !== undefined && status === "success"
        ? items.find((item) => item.id === viewParam)
        : undefined;
    const token = normalizeOrder(spec, refusedToken);
    if (
      view !== undefined &&
      token !== undefined &&
      normalizeOrder(spec, view.config.order) === token &&
      !refused.has(view.id)
    ) {
      refusedSet = new Set(refused).add(view.id);
      setRefused(refusedSet);
    }
    const withoutOrder = addressWithoutOrder(spec, current);
    if (viewParam !== undefined && status === "success" && view === undefined && !unknownOffline) {
      navigate(addressWithoutView(withoutOrder));
      return;
    }
    const applied = view === undefined ? undefined : resolveFor(view, refusedSet);
    navigate(withOrderToken(spec, withoutOrder, applied));
  };

  /** The screen calls this from either arm of D-301's fallback. Before the
   * views read has answered, the view's config is unknown, so the drop waits
   * for it: dropped now, the order-token rule would have to write the default
   * token, and a pasted `?view=<id>&<filter>&order=-total_minor` would end
   * with `order=` where the view as this reader applies it names none. */
  const dropRefusedOrder = (refusedToken?: string) => {
    if (viewParam !== undefined && status === "pending") {
      setPendingDrop({ token: refusedToken });
      return;
    }
    performDrop(refusedToken);
  };

  // The latest render's values, for the effects and for writes that land
  // after an await.
  const latest = useRef({ spec, current, shown, options, resolveFor, performDrop, navigate });
  latest.current = { spec, current, shown, options, resolveFor, performDrop, navigate };

  useEffect(() => {
    if (pendingDrop === null || status === "pending") return;
    setPendingDrop(null);
    latest.current.performDrop(pendingDrop.token);
  }, [pendingDrop, status]);

  // What Reset would write, and whether that changes anything (R1). With a
  // view active it re-applies the view with any remembered refusal forgotten,
  // exactly as `reset` below does; with none it writes the bare list and the
  // default columns. The columns compare by what the table renders. Nothing
  // is offered before the read has answered, after it has failed (W5), or
  // while an arrival is still to apply (W1).
  let canReset = false;
  if (status === "success" && !arrivalPending) {
    if (activeView !== undefined) {
      const refusedSet = new Set(refused);
      refusedSet.delete(activeView.id);
      const target = appliedAddress(spec, shown, activeView.id, resolveFor(activeView, refusedSet));
      canReset =
        addressKey(spec, target) !== shownKey ||
        !sameColumnLayout(columns, rows, columnState, activeView.config.columns);
    } else {
      canReset =
        addressKey(spec, bareAddress(spec, shown)) !== shownKey ||
        !sameColumnLayout(columns, rows, columnState, []);
    }
  }

  const reset = () => {
    if (activeView !== undefined) {
      // Reset sends the stored order again, so a refusal the screen
      // remembered is forgotten and the server answers afresh (item 7).
      const refusedSet = new Set(refused);
      refusedSet.delete(activeView.id);
      setRefused(refusedSet);
      applyView(activeView, refusedSet);
      return;
    }
    setNotice(undefined);
    setColumnState([]);
    navigate(bareAddress(spec, shown));
  };

  const save = async (idempotencyKey: string) => {
    const view = activeView;
    if (view === undefined || !view.mutable || view.is_system) return;
    const config = configFromState(
      spec,
      shown,
      mergeColumnConfig(view.config.columns, columnState),
    );
    const saved = await update.mutateAsync({
      id: view.id,
      body: { config } as unknown as SavedViewPatchRequest,
      idempotencyKey,
    });
    setColumnState(saved.config.columns);
  };

  const saveAsNew = async (input: SaveAsNewInput): Promise<SavedView> => {
    const config = configFromState(spec, shown, columnState);
    const body = {
      screen_key: screenKey,
      name: input.name.trim(),
      shared: input.shared,
      config,
    } as unknown as SavedViewCreateRequest;
    const created = await create.mutateAsync({ body, idempotencyKey: input.idempotencyKey });
    const { shown: now, navigate: go } = latest.current;
    setArrival({ id: created.id, phase: "applied" });
    setNotice(undefined);
    go({ ...now, view: created.id });
    return created;
  };

  const rename = async (id: string, name: string, idempotencyKey: string) => {
    await update.mutateAsync({ id, body: { name: name.trim() }, idempotencyKey });
  };

  const setShared = async (id: string, shared: boolean, idempotencyKey: string) => {
    await update.mutateAsync({ id, body: { shared }, idempotencyKey });
  };

  const remove = async (id: string, idempotencyKey: string) => {
    // The reader asked for this view gone, so finding it missing from here on
    // is not news to them (item 3). It is recorded before the request: a
    // delete whose answer is lost may have landed, and the list read again
    // after it then strips `view` from the address with no notice.
    suppressGone.current.add(id);
    await del.mutateAsync({ id, idempotencyKey });
    const { shown: now, navigate: go } = latest.current;
    if (viewParamOf(now) === id) {
      // The reader deleted the view they have applied: `view` leaves the
      // address, the filters stay, the columns return to the defaults, and
      // no notice shows (item 3), whoever deleted it.
      setColumnState([]);
      setNotice(undefined);
      go(addressWithoutView(now));
    }
  };

  const columnConfig: DataTableColumnConfig = {
    value: columnState,
    ...(desktop
      ? {
          onResize: (key: string, width: number | undefined) => {
            setColumnState((prev) => resizedEntries(prev, key, width));
          },
        }
      : {}),
    resizeLabel: (header: string) => t("views.columns.resize", { column: header }),
  };

  return {
    listEnabled,
    settled: status !== "pending" && !arrivalPending && !waitingForAddress,
    write,
    dropRefusedOrder,
    columnConfig,
    activeView,
    modified,
    spec,
    status,
    error: readError,
    offline,
    canReset,
    groups,
    row: chipRow(groups, activeView?.id),
    isPressed,
    allPressed: isAllPressed(spec, shown),
    notice,
    namesView: shownViewParam !== undefined,
    desktop,
    filterLabel: (key: string) => filterLabels?.[key],
    columns,
    rows,
    rowsLoading,
    columnState,
    setColumnState,
    press: (view) => {
      if (isPressed(view)) clearToBare();
      else applyView(view);
    },
    // A list whose buttons only apply has no un-press: pressing the applied
    // view applies it again in full, as Reset does (V6, spec 16 §4).
    apply: (view) => {
      if (activeView?.id === view.id) reset();
      else applyView(view);
    },
    pressAll: clearToBare,
    reset,
    retry: () => {
      void list.refetch();
    },
    retrying: status === "error" && fetching,
    save,
    saveAsNew,
    rename,
    setShared,
    remove,
    saving: update.isPending || create.isPending || del.isPending,
  };
}
